import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { getPplTalkMessagesEurW } from '../firebaseConfig';
import { useAuth } from '../hooks/useAuth';
import { useAuthGate } from '../hooks/useAuthGate';
import { useMessageCache } from '../contexts/MessageCacheContext';
import SEO from '../components/SEO';
import CreatePostModal from '../components/CreatePostModal';
import LikeButton from '../components/LikeButton';
import MonoAvatar from '../components/MonoAvatar';
import { getMessageUrl, getUserProfileUrl, getUserDisplayName } from '../utils/messageUtils';
import { formatTimestamp } from '../utils/dateUtils';
import { PPLTALK_CATEGORIES } from '../types/pplTalk';
import { Search, Plus, X, MessageCircle, Share2, Flag } from 'react-feather';
import '../styles/Posts.css';

const CATEGORY_COLORS = {
  'just talking': 'violet',
  'relationships': 'pink',
  'personal': 'violet',
  'support': 'cyan',
  'check-ins': 'cyan',
  'wins': 'amber',
  'gratitude': 'amber',
  'advice': 'pink',
  'venting': 'pink',
  'mental health': 'cyan',
  'social connection': 'violet',
  'general': 'violet',
};

const Home = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [filterCategory, setFilterCategory] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [lastMessageId, setLastMessageId] = useState(null);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { user } = useAuth();
  const { requireAuth } = useAuthGate();
  const { getCachedMessages, setCachedMessages, clearAllCache } = useMessageCache();
  const navigate = useNavigate();
  const location = useLocation();
  const loadingRef = useRef(false);
  const feedRef = useRef(null);
  const touchStartY = useRef(0);
  const isPulling = useRef(false);
  const pullDistanceRef = useRef(0);

  const MESSAGES_PER_PAGE = 40;

  // Determine sort mode based on route
  const getSortMode = () => {
    switch (location.pathname) {
      case '/new':
        return { field: 'timestamp', direction: 'desc', label: 'newest' };
      case '/discussed':
        return { field: 'replies', direction: 'desc', label: 'most discussed' };
      default:
        return { field: 'rank', direction: 'desc', label: 'top' };
    }
  };

  const sortMode = getSortMode();

  const loadMessages = useCallback(async (isLoadMore = false) => {
    if (loadingRef.current) return;
    loadingRef.current = true;

    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setMessages([]);
        setLastMessageId(null);
        setHasMore(true);
      }

      let sortModeString;
      switch (sortMode.label) {
        case 'newest':
          sortModeString = 'new';
          break;
        case 'most discussed':
          sortModeString = 'discussed';
          break;
        case 'top':
        default:
          sortModeString = 'top';
          break;
      }

      const cacheParams = {
        sortMode: sortModeString,
        filterCategory: filterCategory,
        lastDocId: isLoadMore ? lastMessageId : null
      };

      if (!isLoadMore) {
        const cachedData = getCachedMessages(cacheParams);
        if (cachedData) {
          setMessages(cachedData.messages);
          setHasMore(cachedData.hasMore);
          setLastMessageId(cachedData.lastMessageId);
          setLoading(false);
          setLoadingMore(false);
          loadingRef.current = false;
          return;
        }
      }

      const result = await getPplTalkMessagesEurW({
        sortMode: sortModeString,
        limit: MESSAGES_PER_PAGE,
        filterCategory: filterCategory,
        lastDocId: isLoadMore ? lastMessageId : null
      });

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to load messages');
      }

      const { messages: newMessages, hasMore: moreAvailable, lastMessageId: newLastMessageId } = result.data;

      if (isLoadMore) {
        setMessages(prev => [...prev, ...newMessages]);
      } else {
        setMessages(newMessages);
        setCachedMessages(cacheParams, {
          messages: newMessages,
          hasMore: moreAvailable,
          lastMessageId: newLastMessageId
        });
      }

      setHasMore(moreAvailable);
      setLastMessageId(newLastMessageId);

    } catch (e) {
      console.error('Failed to load messages', e);
      if (e.code === 'invalid-argument') {
        setError(`Invalid request: ${e.message}`);
      } else if (e.code === 'internal') {
        setError('Server error. Please try again later.');
      } else {
        setError('Unable to load messages');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingRef.current = false;
    }
  }, [filterCategory, lastMessageId, sortMode, getCachedMessages, setCachedMessages]);

  useEffect(() => {
    loadMessages(false);
  }, [user, filterCategory, location.pathname, loadMessages]);

  // Pull-to-refresh (mobile touch only)
  const PULL_THRESHOLD = 60;

  useEffect(() => {
    const feed = feedRef.current;
    if (!feed) return;

    const handleTouchStart = (e) => {
      if (feed.scrollTop <= 0 && !isRefreshing) {
        touchStartY.current = e.touches[0].clientY;
        isPulling.current = true;
      }
    };

    const handleTouchMove = (e) => {
      if (!isPulling.current || isRefreshing) return;
      const dy = e.touches[0].clientY - touchStartY.current;
      if (dy > 0 && feed.scrollTop <= 0) {
        const dist = Math.min(dy * 0.5, 100);
        pullDistanceRef.current = dist;
        setPullDistance(dist);
      } else {
        isPulling.current = false;
        pullDistanceRef.current = 0;
        setPullDistance(0);
      }
    };

    const handleTouchEnd = () => {
      if (!isPulling.current) return;
      isPulling.current = false;
      if (pullDistanceRef.current >= PULL_THRESHOLD) {
        setIsRefreshing(true);
        pullDistanceRef.current = 0;
        setPullDistance(0);
        clearAllCache();
        loadMessages(false).finally(() => {
          setIsRefreshing(false);
        });
      } else {
        pullDistanceRef.current = 0;
        setPullDistance(0);
      }
    };

    feed.addEventListener('touchstart', handleTouchStart, { passive: true });
    feed.addEventListener('touchmove', handleTouchMove, { passive: true });
    feed.addEventListener('touchend', handleTouchEnd, { passive: true });
    return () => {
      feed.removeEventListener('touchstart', handleTouchStart);
      feed.removeEventListener('touchmove', handleTouchMove);
      feed.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isRefreshing, clearAllCache, loadMessages]);

  const isSearching = searchQuery.trim().length > 0;

  // Infinite scroll
  const observerRef = useRef();
  const lastMessageElementRef = useCallback(node => {
    if (loading || loadingMore || isSearching) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loadingRef.current && !isSearching) {
        loadMessages(true);
      }
    }, { threshold: 0.1 });
    if (node) observerRef.current.observe(node);
  }, [loading, loadingMore, hasMore, loadMessages, isSearching]);

  // Filter messages based on search query
  const getFilteredMessages = () => {
    if (!searchQuery.trim()) return messages;
    const query = searchQuery.toLowerCase().trim();
    return messages.filter(message => {
      if (message.categories && message.categories.some(cat => cat.toLowerCase().includes(query))) return true;
      if ((message.userName && message.userName.toLowerCase().includes(query)) ||
          (message.tetherHandle && message.tetherHandle.toLowerCase().includes(query)) ||
          (message.tetherHAndle && message.tetherHAndle.toLowerCase().includes(query))) return true;
      if (message.title && message.title.toLowerCase().includes(query)) return true;
      if (message.body && message.body.toLowerCase().includes(query)) return true;
      return false;
    });
  };

  const filteredMessages = getFilteredMessages();

  const getSEOData = () => {
    const baseTitle = 'pplkpr - Build Deeper Connections';
    const baseDescription = 'Join meaningful conversations about relationships, personal growth, and life transitions. Share experiences and connect with others on pplkpr.';

    if (sortMode.label === 'newest') {
      return { title: 'Latest Discussions | pplkpr', description: 'Explore the newest conversations and discussions on pplkpr. Fresh perspectives on relationships, life, and personal growth.', url: 'https://pplkpr.ai/new' };
    } else if (sortMode.label === 'most discussed') {
      return { title: 'Popular Discussions | pplkpr', description: 'Join the most active conversations on pplkpr. See what topics are generating the most engagement and discussion.', url: 'https://pplkpr.ai/discussed' };
    } else if (filterCategory) {
      return { title: `${filterCategory} - Discussions | pplkpr`, description: `Browse discussions about ${filterCategory}. Connect with others and share your experiences on pplkpr.`, url: `https://pplkpr.ai/?category=${filterCategory}` };
    }
    return { title: baseTitle, description: baseDescription, url: 'https://pplkpr.ai/' };
  };

  const seoData = getSEOData();

  const sortSubtitle = sortMode.label === 'top'
    ? 'what people are talking about'
    : sortMode.label === 'newest'
    ? 'just shared'
    : 'most replied to';

  return (
    <div className="feed" ref={feedRef}>
      <SEO title={seoData.title} description={seoData.description} url={seoData.url} />

      <div className="page-shell">
        <div className="page-shell__inner" style={{ maxWidth: 760 }}>

          {/* Pull-to-refresh indicator */}
          {(pullDistance > 0 || isRefreshing) && (
            <div className="pull-to-refresh-indicator" style={{ height: isRefreshing ? 48 : pullDistance }}>
              {isRefreshing ? (
                <div className="ptr-spinner" />
              ) : (
                <svg className="ptr-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FF00A8" strokeWidth="2"
                  style={{ opacity: Math.min(pullDistance / 60, 1), transform: pullDistance >= 60 ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              )}
            </div>
          )}

          {/* Page header */}
          <div className="posts-page-header">
            <div>
              <h1>posts</h1>
              <p className="posts-page-subtitle">{sortSubtitle}</p>
            </div>
            <button className="primary-button" onClick={requireAuth(() => setShowCreatePost(true))}>
              <Plus size={14} />
              <span>create post</span>
            </button>
          </div>

          {/* Sort tabs + search */}
          <div className="posts-sort-row">
            <div className="posts-pill-tabs">
              <button className={`posts-pill-tab ${sortMode.label === 'top' ? 'active' : ''}`} onClick={() => navigate('/')}>top</button>
              <button className={`posts-pill-tab ${sortMode.label === 'newest' ? 'active' : ''}`} onClick={() => navigate('/new')}>new</button>
              <button className={`posts-pill-tab ${sortMode.label === 'most discussed' ? 'active' : ''}`} onClick={() => navigate('/discussed')}>discussed</button>
            </div>
            <div className="posts-search-input">
              <Search size={14} style={{ color: 'var(--text-dim)' }} />
              <input
                type="text"
                placeholder="search posts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoCapitalize="none"
                autoCorrect="off"
              />
            </div>
          </div>

          {/* Category chips */}
          <div className="posts-category-chips">
            <button
              className={`posts-category-chip ${activeFilter === 'all' && !filterCategory ? 'active' : ''}`}
              onClick={() => { setActiveFilter('all'); setFilterCategory(null); }}
            >
              all
            </button>
            {PPLTALK_CATEGORIES.map((category) => (
              <button
                key={category}
                className={`posts-category-chip ${filterCategory === category ? 'active' : ''}`}
                onClick={() => {
                  setActiveFilter('category');
                  const newCategory = filterCategory === category ? null : category;
                  setFilterCategory(newCategory);
                  if (!newCategory) setActiveFilter('all');
                }}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Create Post Modal */}
          <CreatePostModal
            isOpen={showCreatePost}
            onClose={() => setShowCreatePost(false)}
            onPostCreated={() => { clearAllCache(); loadMessages(false); }}
          />

          {/* Loading state */}
          {loading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[1, 2, 3].map((i) => (
                <div key={i} className="glass-panel" style={{ padding: 18 }}>
                  <div className="skeleton-line wide" style={{ marginBottom: 12 }} />
                  <div className="skeleton-line wide" style={{ marginBottom: 8 }} />
                  <div className="skeleton-line" style={{ width: '60%' }} />
                </div>
              ))}
            </div>
          )}

          {/* Error state */}
          {error && !loading && (
            <div className="glass-panel" style={{ textAlign: 'center', padding: 32 }}>
              <p style={{ color: '#ff6b6b', margin: '0 0 12px' }}>{error}</p>
              {!user && (
                <Link to="/login" className="primary-button" style={{ textDecoration: 'none' }}>login to view top posts</Link>
              )}
            </div>
          )}

          {/* Posts feed */}
          {!loading && !error && (
            <>
              {filteredMessages.length > 0 ? (
                <div className="posts-feed">
                  {filteredMessages.map((m, idx) => {
                    const isLastItem = idx === filteredMessages.length - 1;
                    const shouldAddRef = isLastItem && !isSearching;
                    return (
                      <div
                        key={m.id}
                        className="post-card"
                        onClick={() => navigate(getMessageUrl(m))}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(getMessageUrl(m)); } }}
                        ref={shouldAddRef ? lastMessageElementRef : null}
                      >
                        {/* Author row */}
                        <div className="post-card-author">
                          <MonoAvatar name={getUserDisplayName(m)} size={32} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="post-card-author-name"
                              onClick={(e) => { e.stopPropagation(); navigate(getUserProfileUrl(m)); }}
                              role="button"
                              tabIndex={0}
                            >
                              {getUserDisplayName(m)}
                            </div>
                            <div className="post-card-author-time">{formatTimestamp(m.timestamp)}</div>
                          </div>
                        </div>

                        {/* Title & body */}
                        {m.title && <h3 className="post-card-title">{m.title}</h3>}
                        {m.body && <p className="post-card-body">{m.body}</p>}

                        {/* Category tags */}
                        {Array.isArray(m.categories) && m.categories.length > 0 && (
                          <div className="post-tags">
                            {m.categories.slice(0, 5).map(c => (
                              <span key={c} className={`tag-pill tag-pill--sm tag-pill--${CATEGORY_COLORS[c] || 'violet'}`}>{c}</span>
                            ))}
                          </div>
                        )}

                        {/* Stats row */}
                        <div className="post-stats-row" onClick={(e) => e.stopPropagation()}>
                          <LikeButton
                            messageId={m.id}
                            likes={m.likes || 0}
                            likedBy={m.likedBy || []}
                            size="small"
                            onLikeChange={(newLikes, newLikedBy) => {
                              setMessages(prev => prev.map(msg =>
                                msg.id === m.id ? { ...msg, likes: newLikes, likedBy: newLikedBy } : msg
                              ));
                            }}
                          />
                          <span className="post-stat">
                            <MessageCircle size={14} />
                            <span>{m.replies || 0} replies</span>
                          </span>
                        </div>
                      </div>
                    );
                  })}

                  {/* Loading more */}
                  {loadingMore && !isSearching && (
                    <div className="glass-panel" style={{ textAlign: 'center', padding: 16, color: 'var(--text-muted)', fontSize: 13 }}>
                      loading more posts...
                    </div>
                  )}

                  {/* End of results */}
                  {!hasMore && filteredMessages.length > 0 && !isSearching && (
                    <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--text-dim)', fontSize: 12 }}>
                      you've reached the end
                    </div>
                  )}

                  {/* Search results count */}
                  {isSearching && (
                    <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--text-dim)', fontSize: 12 }}>
                      showing {filteredMessages.length} results from loaded messages
                      {hasMore ? ' — load more to search additional messages' : ''}
                    </div>
                  )}
                </div>
              ) : (
                <div className="glass-panel" style={{ textAlign: 'center', padding: 40 }}>
                  <p style={{ color: '#fff', fontSize: 15, fontWeight: 600, margin: '0 0 8px' }}>
                    {searchQuery.trim() ? 'no search results found' : 'no messages yet'}
                  </p>
                  <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0 }}>
                    {searchQuery.trim() ? 'try adjusting your search terms' : 'check back later for new posts'}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Mobile FAB */}
      {user && (
        <button
          className="fab-post-btn"
          onClick={() => setShowCreatePost(true)}
          type="button"
          aria-label="Create a post"
        >
          <span className="fab-post-text">post</span>
        </button>
      )}
    </div>
  );
};

export default Home;
